import type { User } from '../../types/context.types';
import { getStoredQueryParams } from '../../utils/queryParamStore';
import { EVENT_CODES } from './eventCodes';
import {
  ANALYTICS_PREFERENCE_CHANGE_EVENT,
  getAnalyticsUserOptOut,
  isAnalyticsCollectionEnabled,
  subscribeAnalyticsPreferenceChange,
} from './analyticsGate';
import {
  identifyUser,
  initAnalytics,
  registerAnalyticsProvider,
  resetUser,
  setAnalyticsUserOptOut,
  trackEvent,
  trackPageView,
  acceptAnalyticsConsent,
  declineAnalyticsConsent,
} from './hub';

export {
  initAnalytics,
  registerAnalyticsProvider,
  identifyUser,
  resetUser,
  setAnalyticsUserOptOut,
  trackPageView,
  trackEvent,
  acceptAnalyticsConsent,
  declineAnalyticsConsent,
  isAnalyticsCollectionEnabled,
  getAnalyticsUserOptOut,
  subscribeAnalyticsPreferenceChange,
  ANALYTICS_PREFERENCE_CHANGE_EVENT,
};
export { EVENT_CODES, type AnalyticsEventCode } from './eventCodes';
export { analyticsUserId } from './userId';
export {
  hasConsentBeenGiven,
  isConsentAccepted,
  CONSENT_CHANGE_EVENT,
} from './consentStore';
export {
  COOKIE_CONSENT_BANNER_ENABLED,
  COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS,
} from './consentFeatureFlags';

export function trackSignup(method: string, opts?: { user_id: string }) {
  const stored = getStoredQueryParams();
  trackEvent(EVENT_CODES.SIGN_UP, {
    method,
    ...(opts?.user_id != null ? { user_id: opts.user_id } : {}),
    ...(stored.utm_source ? { utm_source: stored.utm_source } : {}),
    ...(stored.utm_medium ? { utm_medium: stored.utm_medium } : {}),
    ...(stored.utm_campaign ? { utm_campaign: stored.utm_campaign } : {}),
  });
}

export function trackLogin(method: string, opts?: { user_id?: string; success?: boolean }) {
  trackEvent(EVENT_CODES.SIGN_IN, {
    method,
    success: opts?.success ?? true,
    ...(opts?.user_id != null ? { user_id: opts.user_id } : {}),
  });
}

export function trackCTAClick(ctaName: string, location: string) {
  trackEvent(EVENT_CODES.CTA_CLICK, {
    category: 'engagement',
    label: `${ctaName}_${location}`,
  });
}

export function trackHeroCtaClick(label: string) {
  trackEvent(EVENT_CODES.CTA_CLICK, {
    category: 'engagement',
    label,
  });
}

export function trackPlanCreated(label: string) {
  trackEvent(EVENT_CODES.PLAN_CREATED, {
    category: 'engagement',
    label,
  });
}

export type CreatePlanFunnelStep =
  | 'dialog_opened'
  | 'step1_completed'
  | 'submit_started'
  | 'dialog_closed_success'
  | 'dialog_closed_dismissed'
  | 'dialog_closed_error';

export type CreatePlanErrorType =
  | 'validation_step1'
  | 'validation_country'
  | 'validation_email'
  | 'session_create_failed'
  | 'session_email_conflict'
  | 'profile_save_failed'
  | 'session_expired'
  | 'quota_exceeded'
  | 'plan_create_failed';

export function trackCreatePlanFunnel(step: CreatePlanFunnelStep) {
  trackEvent(EVENT_CODES.CREATE_PLAN_FUNNEL, { step });
}

export function trackCreatePlanError(args: {
  error_type: CreatePlanErrorType;
  step?: 1 | 2;
  error_code?: string | number;
}) {
  trackEvent(EVENT_CODES.CREATE_PLAN_ERROR, args);
}

export function trackAssessmentStart(objective_id: string) {
  trackEvent(EVENT_CODES.ASSESSMENT_START, { objective_id });
}

export function trackAssessmentComplete(bucket: string, overall_score: number) {
  trackEvent(EVENT_CODES.ASSESSMENT_COMPLETE, { bucket, overall_score });
}

export function trackAssessmentReportViewed(session_id: number) {
  trackEvent(EVENT_CODES.ASSESSMENT_REPORT_VIEWED, { session_id });
}

export function trackAssessmentPDFDownloaded(session_id: number) {
  trackEvent(EVENT_CODES.ASSESSMENT_PDF_DOWNLOADED, { session_id });
}

export function trackAssessmentShareCreated(session_id: number) {
  trackEvent(EVENT_CODES.ASSESSMENT_SHARE_CREATED, { session_id });
}

export function trackAssessmentPlanCTAClicked(session_id: number) {
  trackEvent(EVENT_CODES.ASSESSMENT_PLAN_CTA_CLICKED, { session_id });
}

export function trackPurchase(payload: {
  user_id: string;
  transaction_id: string;
  revenue: number;
  currency: string;
}) {
  trackEvent(EVENT_CODES.PURCHASE, {
    user_id: payload.user_id,
    transaction_id: payload.transaction_id,
    revenue: payload.revenue,
    currency: payload.currency,
  });
}

export function trackSubscriptionConversion(conversionType: string, conversionValue?: number) {
  trackEvent(EVENT_CODES.CONVERSION, {
    conversion_type: conversionType,
    conversion_value: conversionValue,
  });
}

export function trackAppError(payload: {
  error_type: string;
  error_message: string;
  error_code?: string;
  user?: User | null;
}) {
  trackEvent(EVENT_CODES.ERROR, {
    error_type: payload.error_type,
    error_message: payload.error_message,
    ...(payload.error_code != null ? { error_code: payload.error_code } : {}),
    user: payload.user ?? null,
  });
}

export function trackHomepageFormStarted() {
  trackEvent(EVENT_CODES.CTA_CLICK, { category: 'homepage_form', label: 'homepage_form_started' });
}

export function trackHomepageFormSubmitted() {
  trackEvent(EVENT_CODES.CTA_CLICK, { category: 'homepage_form', label: 'homepage_form_submitted' });
}

export function trackSamplePlanPreviewClicked(topicKey: string) {
  trackEvent(EVENT_CODES.CTA_CLICK, { category: 'sample_plan_preview', label: `sample_plan_preview_clicked_${topicKey}` });
}

export function trackSamplePlanSectionViewed() {
  trackEvent(EVENT_CODES.CTA_CLICK, { category: 'sample_plan_preview', label: 'sample_plan_section_viewed' });
}

export function trackPlatformSwitch(args: { from: 'academy' | 'studio'; to: 'academy' | 'studio' }) {
  trackEvent(EVENT_CODES.PLATFORM_SWITCH, args);
}

export function trackStudioLaunchStarted() {
  trackEvent(EVENT_CODES.STUDIO_LAUNCH_STARTED, {});
}

export function trackStudioLaunchCompleted() {
  trackEvent(EVENT_CODES.STUDIO_LAUNCH_COMPLETED, {});
}

export function trackStudioLaunchFailed(errorMessage: string) {
  trackEvent(EVENT_CODES.STUDIO_LAUNCH_FAILED, { error_message: errorMessage });
}

export function trackCertificateNudgeToastShown(projectId: number, certificateAccess: boolean) {
  trackEvent(EVENT_CODES.CERTIFICATE_NUDGE_TOAST_SHOWN, {
    project_id: projectId,
    certificate_access: certificateAccess,
  });
}

export function trackCertificateNudgeToastDismissed(projectId: number) {
  trackEvent(EVENT_CODES.CERTIFICATE_NUDGE_TOAST_DISMISSED, { project_id: projectId });
}

export function trackCertificateNudgeToastCtaClick(projectId: number, certificateAccess: boolean) {
  trackEvent(EVENT_CODES.CERTIFICATE_NUDGE_TOAST_CTA_CLICK, {
    project_id: projectId,
    certificate_access: certificateAccess,
  });
  trackPlatformSwitch({ from: 'academy', to: 'studio' });
}

export function trackCertificateNudgeBellOpen(pendingCount: number) {
  trackEvent(EVENT_CODES.CERTIFICATE_NUDGE_BELL_OPEN, { pending_count: pendingCount });
}

export function trackCertificateNudgeBellCtaClick(
  projectId: number,
  certificateAccess: boolean,
) {
  trackEvent(EVENT_CODES.CERTIFICATE_NUDGE_BELL_CTA_CLICK, {
    project_id: projectId,
    certificate_access: certificateAccess,
  });
  trackPlatformSwitch({ from: 'academy', to: 'studio' });
}
